// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // impersonatedUser เก็บไว้ใน state เพื่อ trigger re-render เมื่อ session restore
  // eslint-disable-next-line no-unused-vars
  const [impersonatedUser, setImpersonatedUser] = useState(() => {
    const saved = sessionStorage.getItem('wadphol_impersonated');
    if (saved) { try { return JSON.parse(saved); } catch { sessionStorage.removeItem('wadphol_impersonated'); } }
    return null;
  });
  // realAdmin จะ set เมื่อ fetchUser สำเร็จ + มี impersonatedUser ใน session
  const [realAdmin, setRealAdmin] = useState(null);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('wadphol_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await getMe();
      if (res.data.success) {
        const adminUser = res.data.user;
        setRealAdmin(null); // reset ก่อน
        // ถ้ามี impersonatedUser บันทึกไว้ใน session ให้ restore state
        const savedImp = sessionStorage.getItem('wadphol_impersonated');
        if (savedImp && adminUser.role === 'admin') {
          try {
            const impUser = JSON.parse(savedImp);
            setRealAdmin(adminUser);
            setUser(impUser);
          } catch {
            sessionStorage.removeItem('wadphol_impersonated');
            setUser(adminUser);
          }
        } else {
          sessionStorage.removeItem('wadphol_impersonated');
          setUser(adminUser);
        }
      }
    } catch {
      localStorage.removeItem('wadphol_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = (token, userData) => {
    localStorage.setItem('wadphol_token', token);
    setUser(userData);
    setRealAdmin(null);
    setImpersonatedUser(null);
    sessionStorage.removeItem('wadphol_impersonated');
  };

  const logout = () => {
    localStorage.removeItem('wadphol_token');
    sessionStorage.removeItem('wadphol_impersonated');
    setUser(null);
    setRealAdmin(null);
    setImpersonatedUser(null);
  };

  // เข้าระบบในนามครูคนนี้ (ใช้ token เดิมของ admin แต่เปลี่ยน user object)
  const impersonate = (targetUser) => {
    if (!user || user.role !== 'admin') return;
    sessionStorage.setItem('wadphol_impersonated', JSON.stringify(targetUser));
    setRealAdmin(user);
    setImpersonatedUser(targetUser);
    setUser(targetUser);
  };

  // กลับมาเป็น admin
  const stopImpersonate = () => {
    if (!realAdmin) return;
    setUser(realAdmin);
    setRealAdmin(null);
    setImpersonatedUser(null);
    sessionStorage.removeItem('wadphol_impersonated');
  };

  const isImpersonating = !!realAdmin;

  const hasRole = (...roles) => user && roles.includes(user.role);
  // effectiveRole = role ที่ใช้สำหรับ UI (เมนู, ปุ่ม, สิทธิ์)
  // เมื่อ impersonate → ใช้ role ของ user ที่กำลังสวมรอย (teacher)
  // PrivateRoute จะใช้ realAdmin.role แยกต่างหากเพื่อป้องกัน redirect
  const effectiveRole = user?.role;
  const isAdmin    = () => effectiveRole === 'admin';
  const isAcademic = () => ['admin', 'director', 'academic', 'assessment'].includes(effectiveRole);
  const isTeacher  = () => ['admin', 'director', 'academic', 'assessment', 'teacher'].includes(effectiveRole);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, hasRole, isAdmin, isAcademic, isTeacher,
      impersonate, stopImpersonate, isImpersonating, realAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
